import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type Comment = Database['public']['Tables']['comments']['Row'];

export interface CommentWithProfile extends Comment {
  profiles: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    headline: string | null;
  } | null;
  replies?: CommentWithProfile[];
  isLikedByUser?: boolean;
}

export function useComments(postId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['comments', postId],
    queryFn: async (): Promise<CommentWithProfile[]> => {
      // Get comments
      const { data: comments, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('post_id', postId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;
      if (!comments || comments.length === 0) return [];

      // Get profiles for comment authors
      const userIds = [...new Set(comments.map((c) => c.user_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, full_name, avatar_url, headline')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Get user's likes on these comments
      let userLikedCommentIds: Set<string> = new Set();
      if (user?.id) {
        const commentIds = comments.map((c) => c.id);
        const { data: likes } = await supabase
          .from('comment_likes')
          .select('comment_id')
          .eq('user_id', user.id)
          .in('comment_id', commentIds);

        if (likes) {
          userLikedCommentIds = new Set(likes.map((l) => l.comment_id));
        }
      }

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]));

      // Build nested structure
      const commentsWithProfile: CommentWithProfile[] = comments.map((comment) => {
        const profile = profileMap.get(comment.user_id);
        return {
          ...comment,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                headline: profile.headline,
              }
            : null,
          isLikedByUser: userLikedCommentIds.has(comment.id),
          replies: [],
        };
      });

      // Separate top-level and replies
      const commentMap = new Map(commentsWithProfile.map((c) => [c.id, c]));
      const topLevelComments: CommentWithProfile[] = [];

      for (const comment of commentsWithProfile) {
        if (comment.parent_comment_id) {
          const parent = commentMap.get(comment.parent_comment_id);
          if (parent) {
            parent.replies = parent.replies || [];
            parent.replies.push(comment);
          }
        } else {
          topLevelComments.push(comment);
        }
      }

      return topLevelComments;
    },
    enabled: !!postId,
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      postId,
      content,
      parentCommentId,
    }: {
      postId: string;
      content: string;
      parentCommentId?: string | null;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content,
          parent_comment_id: parentCommentId || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Update post comments_count
      const { count } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId)
        .eq('is_deleted', false);

      await supabase
        .from('posts')
        .update({ comments_count: count || 0 })
        .eq('id', postId);

      return data;
    },
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      postId,
    }: {
      commentId: string;
      postId: string;
    }) => {
      const { error } = await supabase
        .from('comments')
        .update({ is_deleted: true })
        .eq('id', commentId);

      if (error) throw error;
    },
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}
